import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Workflow, Plus, Trash2, ArrowDown, CheckCircle, GitBranch, 
  Square, Loader2, Save, ArrowLeft, Zap, Clock, User, MapPin, AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SWSWorkflowBuilderPage = () => {
  const navigate = useNavigate();
  const { workflowId } = useParams();
  const isEditMode = !!workflowId;

  // Metadata
  const [triggerEvents, setTriggerEvents] = useState([]);
  const [autoCompleteEvents, setAutoCompleteEvents] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locationSources, setLocationSources] = useState([]);
  const [conditionCollections, setConditionCollections] = useState([]);
  const [conditionCheckTypes, setConditionCheckTypes] = useState([]);

  // Workflow data
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [triggerEvent, setTriggerEvent] = useState('');
  const [steps, setSteps] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [isSystemWorkflow, setIsSystemWorkflow] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableVariables, setAvailableVariables] = useState([]);

  const fetchMetadata = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const [eventsRes, autoCompleteRes, rolesRes, locationsRes, collectionsRes, checkTypesRes] = await Promise.all([
        fetch(`${API}/api/sws/metadata/trigger-events`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/sws/metadata/auto-complete-events`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/sws/metadata/roles`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/sws/metadata/location-sources`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/sws/metadata/condition-collections`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/sws/metadata/condition-check-types`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const [events, autoComplete, rolesData, locations, collections, checkTypes] = await Promise.all([
        eventsRes.json(), autoCompleteRes.json(), rolesRes.json(), 
        locationsRes.json(), collectionsRes.json(), checkTypesRes.json()
      ]);

      setTriggerEvents(events.events || []);
      setAutoCompleteEvents(autoComplete.events || []);
      setRoles(rolesData.roles || []);
      setLocationSources(locations.sources || []);
      setConditionCollections(collections.collections || []);
      setConditionCheckTypes(checkTypes.check_types || []);

    } catch (error) {
      console.error('Error fetching metadata:', error);
      toast.error('Failed to load metadata');
    }
  }, []);

  const fetchWorkflow = useCallback(async () => {
    if (!workflowId) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/api/sws/workflows/${workflowId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch workflow');
      
      const workflow = await res.json();
      setWorkflowName(workflow.name);
      setWorkflowDescription(workflow.description || '');
      setTriggerEvent(workflow.trigger_event);
      setSteps(workflow.steps || []);
      setIsActive(workflow.is_active);
      setIsSystemWorkflow(workflow.is_system_workflow || false);
    } catch (error) {
      console.error('Error fetching workflow:', error);
      toast.error('Failed to load workflow');
      navigate('/admin/sws');
    }
  }, [workflowId, navigate]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchMetadata();
      if (isEditMode) {
        await fetchWorkflow();
      }
      setLoading(false);
    };
    loadData();
  }, [fetchMetadata, fetchWorkflow, isEditMode]);

  // Update available variables when trigger event changes
  useEffect(() => {
    const selectedEvent = triggerEvents.find(e => e.value === triggerEvent);
    if (selectedEvent) {
      setAvailableVariables(selectedEvent.variables || []);
    } else {
      setAvailableVariables([]);
    }
  }, [triggerEvent, triggerEvents]);

  const generateStepId = () => `step-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const addStep = (stepType, afterIndex = null) => {
    const newStep = {
      id: generateStepId(),
      sequence: afterIndex !== null ? afterIndex + 2 : steps.length + 1,
      step_type: stepType,
    };

    if (stepType === 'task') {
      newStep.task_config = {
        title: '',
        description: '',
        tat_value: 2,
        tat_unit: 'hours',
        assign_to_role: '',
        location_source: 'trigger_entity',
        auto_complete_event: '',
        next_step_id: null
      };
    } else if (stepType === 'condition') {
      newStep.condition_config = {
        name: '',
        check_type: 'data_exists',
        collection: '',
        match_field: '',
        match_value: '{trigger.id}',
        true_step_id: '',
        false_step_id: ''
      };
    } else if (stepType === 'terminate') {
      newStep.terminate_config = {
        status: 'completed',
        message: 'Workflow completed'
      };
    }

    // Insert at specific position or add to end
    if (afterIndex !== null) {
      const newSteps = [...steps];
      newSteps.splice(afterIndex + 1, 0, newStep);
      // Re-sequence all steps
      setSteps(newSteps.map((step, idx) => ({ ...step, sequence: idx + 1 })));
    } else {
      setSteps([...steps, newStep]);
    }
  };

  const updateStep = (stepId, field, value) => {
    setSteps(steps.map(step => {
      if (step.id !== stepId) return step;
      
      const updated = { ...step };
      
      if (field.startsWith('task_config.')) {
        const configField = field.replace('task_config.', '');
        updated.task_config = { ...updated.task_config, [configField]: value };
      } else if (field.startsWith('condition_config.')) {
        const configField = field.replace('condition_config.', '');
        updated.condition_config = { ...updated.condition_config, [configField]: value };
      } else if (field.startsWith('terminate_config.')) {
        const configField = field.replace('terminate_config.', '');
        updated.terminate_config = { ...updated.terminate_config, [configField]: value };
      } else if (field.startsWith('goto_config.')) {
        const configField = field.replace('goto_config.', '');
        updated.goto_config = { ...updated.goto_config, [configField]: value };
      } else {
        updated[field] = value;
      }
      
      return updated;
    }));
  };

  const removeStep = (stepId) => {
    const newSteps = steps.filter(s => s.id !== stepId).map((step, idx) => ({
      ...step,
      sequence: idx + 1
    }));
    setSteps(newSteps);
  };

  const moveStep = (stepId, direction) => {
    const idx = steps.findIndex(s => s.id === stepId);
    if (idx === -1) return;
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === steps.length - 1) return;

    const newSteps = [...steps];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [newSteps[idx], newSteps[swapIdx]] = [newSteps[swapIdx], newSteps[idx]];
    
    setSteps(newSteps.map((step, i) => ({ ...step, sequence: i + 1 })));
  };

  const getStepOptions = () => {
    return steps.map(s => ({
      value: s.id,
      label: `Step ${s.sequence}: ${s.step_type === 'task' ? s.task_config?.title || 'Task' : s.step_type === 'condition' ? s.condition_config?.name || 'Condition' : 'Terminate'}`
    }));
  };

  const validateWorkflow = () => {
    if (!workflowName.trim()) {
      toast.error('Workflow name is required');
      return false;
    }
    if (!triggerEvent) {
      toast.error('Please select a trigger event');
      return false;
    }
    if (steps.length === 0) {
      toast.error('Please add at least one step');
      return false;
    }
    
    // Validate each step
    for (const step of steps) {
      if (step.step_type === 'task') {
        if (!step.task_config?.title?.trim()) {
          toast.error(`Step ${step.sequence}: Task title is required`);
          return false;
        }
        if (!step.task_config?.assign_to_role) {
          toast.error(`Step ${step.sequence}: Assign to role is required`);
          return false;
        }
      } else if (step.step_type === 'condition') {
        if (!step.condition_config?.name?.trim()) {
          toast.error(`Step ${step.sequence}: Condition name is required`);
          return false;
        }
        if (!step.condition_config?.collection) {
          toast.error(`Step ${step.sequence}: Collection is required`);
          return false;
        }
      }
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!validateWorkflow()) return;

    const token = localStorage.getItem('token');
    setSaving(true);

    try {
      const payload = {
        name: workflowName.trim(),
        description: workflowDescription.trim(),
        trigger_event: triggerEvent,
        steps: steps,
        is_active: isActive
      };

      const url = isEditMode 
        ? `${API}/api/sws/workflows/${workflowId}`
        : `${API}/api/sws/workflows`;
      
      const method = isEditMode ? 'PATCH' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        let errorMessage = 'Failed to save';
        try {
          const err = await res.json();
          errorMessage = err.detail || errorMessage;
        } catch (parseError) {
          // If we can't parse JSON, use status text
          errorMessage = res.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      toast.success(isEditMode ? 'Workflow updated' : 'Workflow created');
      navigate('/admin/sws');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <div className="space-y-6 pb-20" data-testid="sws-builder-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/sws')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Workflow className="w-6 h-6 text-purple-600" />
              {isEditMode ? 'Edit Workflow' : 'Create Workflow'}
            </h1>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-workflow-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isEditMode ? 'Update' : 'Save'} Workflow
        </Button>
      </div>

      {/* Workflow Details */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Workflow Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Workflow Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                placeholder="e.g., Gate Entry Processing"
                data-testid="workflow-name-input"
                disabled={isSystemWorkflow}
                className={isSystemWorkflow ? 'bg-gray-100' : ''}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trigger Event <span className="text-red-500">*</span>
              </label>
              <Select value={triggerEvent} onValueChange={setTriggerEvent} disabled={isSystemWorkflow}>
                <SelectTrigger data-testid="trigger-event-select" className={isSystemWorkflow ? 'bg-gray-100' : ''}>
                  <SelectValue placeholder="Select trigger event" />
                </SelectTrigger>
                <SelectContent>
                  {triggerEvents.map(event => (
                    <SelectItem key={event.value} value={event.value}>
                      <span className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{event.category}</Badge>
                        {event.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {isSystemWorkflow && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>⚠️ System Workflow:</strong> This is a pre-configured workflow. Only <strong>TAT</strong> and <strong>Assignee Role</strong> can be modified.
              </p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <Textarea
              value={workflowDescription}
              onChange={(e) => setWorkflowDescription(e.target.value)}
              placeholder="Brief description of what this workflow does"
              rows={2}
              disabled={isSystemWorkflow}
              className={isSystemWorkflow ? 'bg-gray-100' : ''}
            />
          </div>

          {availableVariables.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-medium text-blue-700 mb-2">Available Variables:</p>
              <div className="flex flex-wrap gap-2">
                {availableVariables.map(v => (
                  <code key={v} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                    {`{${v}}`}
                  </code>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Steps */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle className="text-base">Workflow Steps</CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => addStep('task')} className="gap-1">
                <CheckCircle className="w-3.5 h-3.5 text-blue-500" />
                Add Task
              </Button>
              <Button size="sm" variant="outline" onClick={() => addStep('condition')} className="gap-1">
                <GitBranch className="w-3.5 h-3.5 text-yellow-500" />
                Add Condition
              </Button>
              <Button size="sm" variant="outline" onClick={() => addStep('terminate')} className="gap-1">
                <Square className="w-3.5 h-3.5 text-gray-500" />
                Add Terminate
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {steps.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed rounded-lg">
              <Workflow className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No steps added yet</p>
              <p className="text-sm text-gray-400">Click the buttons above to add steps</p>
            </div>
          ) : (
            <div className="space-y-4">
              {steps.map((step, idx) => (
                <div key={step.id}>
                  {idx > 0 && (
                    <div className="flex justify-center py-2">
                      <ArrowDown className="w-5 h-5 text-gray-300" />
                    </div>
                  )}
                  
                  <StepCard
                    step={step}
                    stepNumber={idx + 1}
                    totalSteps={steps.length}
                    roles={roles}
                    locationSources={locationSources}
                    autoCompleteEvents={autoCompleteEvents}
                    conditionCollections={conditionCollections}
                    conditionCheckTypes={conditionCheckTypes}
                    stepOptions={getStepOptions()}
                    availableVariables={availableVariables}
                    onUpdate={updateStep}
                    onRemove={removeStep}
                    onMove={moveStep}
                    isSystemWorkflow={isSystemWorkflow}
                  />

                  {/* Inline Add Step Buttons */}
                  <div className="flex justify-center py-3">
                    <div className="inline-flex items-center gap-2 bg-white border rounded-lg px-3 py-2 shadow-sm">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => addStep('task', idx)} 
                        className="gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="Add Task below"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <CheckCircle className="w-3.5 h-3.5" />
                        Task
                      </Button>
                      <div className="h-4 w-px bg-gray-300"></div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => addStep('condition', idx)} 
                        className="gap-1 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                        title="Add Condition below"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <GitBranch className="w-3.5 h-3.5" />
                        Condition
                      </Button>
                      <div className="h-4 w-px bg-gray-300"></div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => addStep('terminate', idx)} 
                        className="gap-1 text-gray-600 hover:text-gray-700 hover:bg-gray-100"
                        title="Add Terminate below"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <Square className="w-3.5 h-3.5" />
                        Terminate
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
};

// Step Card Component
const StepCard = ({ 
  step, stepNumber, totalSteps, roles, locationSources, autoCompleteEvents,
  conditionCollections, conditionCheckTypes, stepOptions, availableVariables,
  onUpdate, onRemove, onMove, isSystemWorkflow = false
}) => {
  const getStepColor = () => {
    switch (step.step_type) {
      case 'task': return 'border-l-blue-500 bg-blue-50/50';
      case 'condition': return 'border-l-yellow-500 bg-yellow-50/50';
      case 'terminate': return 'border-l-gray-500 bg-gray-50/50';
      default: return 'border-l-gray-300';
    }
  };

  const getStepIcon = () => {
    switch (step.step_type) {
      case 'task': return <CheckCircle className="w-5 h-5 text-blue-500" />;
      case 'condition': return <GitBranch className="w-5 h-5 text-yellow-500" />;
      case 'terminate': return <Square className="w-5 h-5 text-gray-500" />;
      default: return null;
    }
  };

  return (
    <Card className={`border-l-4 ${getStepColor()}`} data-testid={`step-card-${step.id}`}>
      <CardContent className="py-4">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-2">
            {getStepIcon()}
            <span className="font-medium">Step {stepNumber}</span>
            <Badge variant="outline" className="text-xs capitalize">{step.step_type}</Badge>
            {isSystemWorkflow && (
              <Badge variant="secondary" className="text-xs">System</Badge>
            )}
          </div>
          <div className="flex gap-1">
            {!isSystemWorkflow && stepNumber > 1 && (
              <Button size="sm" variant="ghost" onClick={() => onMove(step.id, 'up')} className="h-7 px-2">
                ↑
              </Button>
            )}
            {!isSystemWorkflow && stepNumber < totalSteps && (
              <Button size="sm" variant="ghost" onClick={() => onMove(step.id, 'down')} className="h-7 px-2">
                ↓
              </Button>
            )}
            {!isSystemWorkflow && (
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => onRemove(step.id)}
                className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Task Step */}
        {step.step_type === 'task' && (
          <div className="space-y-4">
            {isSystemWorkflow && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-700">
                  <strong>System Workflow:</strong> Only TAT and Assignee Role can be modified.
                </p>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Task Title <span className="text-red-500">*</span>
                </label>
                <Input
                  value={step.task_config?.title || ''}
                  onChange={(e) => onUpdate(step.id, 'task_config.title', e.target.value)}
                  placeholder="e.g., Store Receiving - {gate_entry_no}"
                  disabled={isSystemWorkflow}
                  className={isSystemWorkflow ? 'bg-gray-100' : ''}
                />
                {availableVariables.length > 0 && !isSystemWorkflow && (
                  <p className="text-xs text-gray-400 mt-1">Use variables: {availableVariables.map(v => `{${v}}`).join(', ')}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Input
                  value={step.task_config?.description || ''}
                  onChange={(e) => onUpdate(step.id, 'task_config.description', e.target.value)}
                  placeholder="Optional description"
                  disabled={isSystemWorkflow}
                  className={isSystemWorkflow ? 'bg-gray-100' : ''}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> TAT Value
                </label>
                <Input
                  type="number"
                  min="1"
                  value={step.task_config?.tat_value || 2}
                  onChange={(e) => onUpdate(step.id, 'task_config.tat_value', parseInt(e.target.value) || 1)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">TAT Unit</label>
                <Select 
                  value={step.task_config?.tat_unit || 'hours'} 
                  onValueChange={(v) => onUpdate(step.id, 'task_config.tat_unit', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hours">Hours</SelectItem>
                    <SelectItem value="days">Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <User className="w-3.5 h-3.5" /> Assign To <span className="text-red-500">*</span>
                </label>
                <Select 
                  value={step.task_config?.assign_to_role || ''} 
                  onValueChange={(v) => onUpdate(step.id, 'task_config.assign_to_role', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> Location From
                </label>
                <Select 
                  value={step.task_config?.location_source || 'trigger_entity'} 
                  onValueChange={(v) => onUpdate(step.id, 'task_config.location_source', v)}
                  disabled={isSystemWorkflow}
                >
                  <SelectTrigger className={isSystemWorkflow ? 'bg-gray-100' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {locationSources.map(loc => (
                      <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Zap className="w-3.5 h-3.5" /> Auto-complete When
                </label>
                <Select 
                  value={step.task_config?.auto_complete_event || '__none__'} 
                  onValueChange={(v) => onUpdate(step.id, 'task_config.auto_complete_event', v === '__none__' ? '' : v)}
                  disabled={isSystemWorkflow}
                >
                  <SelectTrigger className={isSystemWorkflow ? 'bg-gray-100' : ''}>
                    <SelectValue placeholder="Manual completion" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Manual completion (no auto)</SelectItem>
                    {autoCompleteEvents.map(event => (
                      <SelectItem key={event.value} value={event.value}>{event.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Next Step</label>
                <Select 
                  value={step.task_config?.next_step_id || '__end__'} 
                  onValueChange={(v) => onUpdate(step.id, 'task_config.next_step_id', v === '__end__' ? null : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="End workflow" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__end__">→ End (no more steps)</SelectItem>
                    {stepOptions.filter(s => s.value !== step.id).map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Condition Step */}
        {step.step_type === 'condition' && (
          <div className="space-y-4">
            {isSystemWorkflow && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-700">
                  <strong>System Workflow:</strong> Condition logic cannot be modified.
                </p>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condition Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={step.condition_config?.name || ''}
                  onChange={(e) => onUpdate(step.id, 'condition_config.name', e.target.value)}
                  placeholder="e.g., BOM exists for item?"
                  disabled={isSystemWorkflow}
                  className={isSystemWorkflow ? 'bg-gray-100' : ''}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check Type</label>
                <Select 
                  value={step.condition_config?.check_type || 'data_exists'} 
                  onValueChange={(v) => onUpdate(step.id, 'condition_config.check_type', v)}
                  disabled={isSystemWorkflow}
                >
                  <SelectTrigger className={isSystemWorkflow ? 'bg-gray-100' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {conditionCheckTypes.map(ct => (
                      <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Field to Check <span className="text-red-500">*</span>
                </label>
                <Input
                  value={step.condition_config?.field_name || ''}
                  onChange={(e) => onUpdate(step.id, 'condition_config.field_name', e.target.value)}
                  placeholder="e.g., has_bom"
                  disabled={isSystemWorkflow}
                  className={isSystemWorkflow ? 'bg-gray-100' : ''}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Collection (optional)</label>
                <Select 
                  value={step.condition_config?.collection || '__none__'} 
                  onValueChange={(v) => onUpdate(step.id, 'condition_config.collection', v === '__none__' ? '' : v)}
                  disabled={isSystemWorkflow}
                >
                  <SelectTrigger className={isSystemWorkflow ? 'bg-gray-100' : ''}>
                    <SelectValue placeholder="Trigger data" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">From trigger data</SelectItem>
                    {conditionCollections.map(col => (
                      <SelectItem key={col.value} value={col.value}>{col.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Input
                  value={step.condition_config?.description || ''}
                  onChange={(e) => onUpdate(step.id, 'condition_config.description', e.target.value)}
                  placeholder="Optional description"
                  disabled={isSystemWorkflow}
                  className={isSystemWorkflow ? 'bg-gray-100' : ''}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 bg-white p-3 rounded-lg border">
              <div>
                <label className="block text-sm font-medium text-green-700 mb-1">If TRUE → Go to</label>
                <Select 
                  value={step.condition_config?.true_step_id || ''} 
                  onValueChange={(v) => onUpdate(step.id, 'condition_config.true_step_id', v)}
                  disabled={isSystemWorkflow}
                >
                  <SelectTrigger className={`border-green-200 ${isSystemWorkflow ? 'bg-gray-100' : ''}`}>
                    <SelectValue placeholder="Select step" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__end__">→ End (complete)</SelectItem>
                    {stepOptions.filter(s => s.value !== step.id).map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-red-700 mb-1">If FALSE → Go to</label>
                <Select 
                  value={step.condition_config?.false_step_id || ''} 
                  onValueChange={(v) => onUpdate(step.id, 'condition_config.false_step_id', v)}
                  disabled={isSystemWorkflow}
                >
                  <SelectTrigger className={`border-red-200 ${isSystemWorkflow ? 'bg-gray-100' : ''}`}>
                    <SelectValue placeholder="Select step" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__end__">→ End (complete)</SelectItem>
                    {stepOptions.filter(s => s.value !== step.id).map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Goto Step */}
        {step.step_type === 'goto' && (
          <div className="space-y-4">
            {isSystemWorkflow && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-700">
                  <strong>System Workflow:</strong> Goto/Loop logic cannot be modified.
                </p>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Step Name</label>
                <Input
                  value={step.goto_config?.name || ''}
                  onChange={(e) => onUpdate(step.id, 'goto_config.name', e.target.value)}
                  placeholder="e.g., Loop back to WO creation"
                  disabled={isSystemWorkflow}
                  className={isSystemWorkflow ? 'bg-gray-100' : ''}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">Go to Step</label>
                <Select 
                  value={step.goto_config?.target_step_id || ''} 
                  onValueChange={(v) => onUpdate(step.id, 'goto_config.target_step_id', v)}
                  disabled={isSystemWorkflow}
                >
                  <SelectTrigger className={`border-purple-200 ${isSystemWorkflow ? 'bg-gray-100' : ''}`}>
                    <SelectValue placeholder="Select target step" />
                  </SelectTrigger>
                  <SelectContent>
                    {stepOptions.filter(s => s.value !== step.id).map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Input
                value={step.goto_config?.description || ''}
                onChange={(e) => onUpdate(step.id, 'goto_config.description', e.target.value)}
                placeholder="Optional description"
                disabled={isSystemWorkflow}
                className={isSystemWorkflow ? 'bg-gray-100' : ''}
              />
            </div>
          </div>
        )}

        {/* Terminate Step */}
        {step.step_type === 'terminate' && (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <Select 
                value={step.terminate_config?.status || 'completed'} 
                onValueChange={(v) => onUpdate(step.id, 'terminate_config.status', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <Input
                value={step.terminate_config?.message || ''}
                onChange={(e) => onUpdate(step.id, 'terminate_config.message', e.target.value)}
                placeholder="Workflow completed"
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SWSWorkflowBuilderPage;
